import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { useThemeStore, Theme } from '../../store/themeStore';
import { useTranslation } from 'react-i18next';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useThemeStore();
  const [selectedTheme, setSelectedTheme] = useState<Theme>(theme);
  const [originalTheme] = useState<Theme>(theme); // Store original theme for cancel
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [originalLanguage] = useState(i18n.language);

  if (!isOpen) return null;

  const handleThemeChange = (newTheme: Theme) => {
    setSelectedTheme(newTheme);
    setTheme(newTheme); // Apply theme immediately
  };

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage);
    i18n.changeLanguage(newLanguage); // Apply language immediately
  };

  const handleSave = () => {
    // Theme and language are already saved, just close modal
    onClose();
  };

  const handleCancel = () => {
    // Revert to original theme and language
    setTheme(originalTheme);
    i18n.changeLanguage(originalLanguage);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('common:settings.title')}</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Language Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">{t('common:settings.language')}</h3>
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="language"
                value="en"
                checked={selectedLanguage === 'en'}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{t('common:languages.en')}</span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="language"
                value="es"
                checked={selectedLanguage === 'es'}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{t('common:languages.es')}</span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="language"
                value="fr"
                checked={selectedLanguage === 'fr'}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{t('common:languages.fr')}</span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="language"
                value="de"
                checked={selectedLanguage === 'de'}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{t('common:languages.de')}</span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="language"
                value="it"
                checked={selectedLanguage === 'it'}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{t('common:languages.it')}</span>
            </label>
          </div>
        </div>

        {/* Theme Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">{t('common:settings.theme')}</h3>
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="theme"
                value="light"
                checked={selectedTheme === 'light'}
                onChange={(e) => handleThemeChange(e.target.value as Theme)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{t('common:settings.light')}</span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={selectedTheme === 'dark'}
                onChange={(e) => handleThemeChange(e.target.value as Theme)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{t('common:settings.dark')}</span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={handleCancel} fullWidth>
            {t('common:buttons.cancel')}
          </Button>
          <Button onClick={handleSave} fullWidth>
            {t('common:buttons.ok')}
          </Button>
        </div>
      </div>
    </div>
  );
};
